import React, { useEffect, useState } from 'react';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { TableRow, mainSliceActions } from 'src/store/mainSlice';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import styles from './style';
import CircularProgress from '@mui/material/CircularProgress';
import SortArrowHollow from 'src/images/svg/SortArrowHollow';
import Header from './Header';
import { useIsRTL } from 'src/hooks/useIsRTL';
import PaginationItem from '@mui/material/PaginationItem';
import { useFetchHtml } from 'src/hooks/useFetchHtml';
import { useResponsiveItemsPerPage } from 'src/hooks/useResponsiveItemsPerPage ';
import AllTags from './AllTags';
import { useFetchArticles } from '../../hooks/useFetchArticles';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { ArticleShareData, encryptArticleData } from 'src/utils/encrypt';
import { IconButton } from '@mui/material';

const Table: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isRTL = useIsRTL();
  useFetchHtml();
  const { hasMore, data, isLoading, currentPage, sortedBy, sortedOrder, noDataMsg, isError, offset, itemsPerPage, systemId, searchInput, sort, isAsc, articleTagId } = useAppSelector(state => state.main);
  const columns: (keyof TableRow)[] = ['RelevantFromDate', 'MainClassificationName', 'ArticleHeader', 'Tags'];
  const rowsPerPage = useResponsiveItemsPerPage();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<TableRow | null>(null);
  const [copiedVisible, setCopiedVisible] = useState(false);
  const [copiedMounted, setCopiedMounted] = useState(false);
  const [copiedPosition, setCopiedPosition] = useState<{ top: number; left: number } | null>(null);
  const hideTimeoutRef = React.useRef<number | null>(null);
  const unmountTimeoutRef = React.useRef<number | null>(null);
  const open = Boolean(anchorEl);

  const articlesPayload = { row: offset, maxRowInPage: itemsPerPage, MainClassificationID: systemId, sort, isAsc, searchKey: searchInput, TagID: articleTagId };
  const { allPagesData, tableData, isMorePages } = useFetchArticles(articlesPayload);

  useEffect(() => {
    dispatch(mainSliceActions.updatePagination());
  }, [dispatch, currentPage, tableData, rowsPerPage]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    console.log(hasMore, tableData.length > rowsPerPage, isMorePages, currentPage);
    dispatch(mainSliceActions.setCurrentPage(page));
    dispatch(mainSliceActions.updatePagination());
  };

  const moveToHtml = (ArticleID: number, index: number) => {
    console.log(ArticleID);
    dispatch(mainSliceActions.setCurrentId(ArticleID));
    dispatch(mainSliceActions.setIsModal(true));
    dispatch(mainSliceActions.setIndex(index));
  };

  const handleSort = (column: string) => {
    dispatch(mainSliceActions.setSortedBy(column));
    dispatch(mainSliceActions.setSort(column));
    dispatch(mainSliceActions.setSortedOrder());
    dispatch(mainSliceActions.setIsAsc());
    dispatch(mainSliceActions.setCurrentPage(1));
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: TableRow) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
    
    const buttonElement = event.currentTarget;
    const buttonRect = buttonElement.getBoundingClientRect();
    console.log("Button.height: ", buttonRect.height);
    setCopiedPosition({
      top: buttonRect.top + (buttonRect.height / 2) - 7,
      left: buttonRect.right + 10,
    });
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleCopyLink = () => {
    if (!selectedRow) return;

    const shareData: ArticleShareData = {
      articleId: selectedRow.ArticleID,
      mainClassificationName: selectedRow.MainClassificationName,
      relevantFromDate: selectedRow.RelevantFromDate,
      articleHeader: selectedRow.ArticleHeader,
      isHeader: true,
    };

    const encryptedData = encryptArticleData(shareData);
    const shareableUrl = `${window.location.origin}?a=${encryptedData}`;

    navigator.clipboard.writeText(shareableUrl).then(() => {
      handleMenuClose();

      // Show copied notification
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      if (unmountTimeoutRef.current) {
        clearTimeout(unmountTimeoutRef.current);
      }

      setCopiedMounted(false);
      setCopiedVisible(false);

      requestAnimationFrame(() => {
        setCopiedMounted(true);
        setCopiedVisible(true);
      });

      hideTimeoutRef.current = window.setTimeout(() => {
        setCopiedVisible(false);
      }, 800);

      unmountTimeoutRef.current = window.setTimeout(() => {
        setCopiedMounted(false);
      }, 1300);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };
  
  return (
    <styles.BodyWrapper>
      <styles.TableWrapper> 
        <Header />

        {isLoading ? 
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20vh'}}>
            <CircularProgress style={{width: '55px', height: '55px'}}/> 
          </div>
        : noDataMsg ? 
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20vh'}}>{t("noSearchData")}</div>
        : isError ?
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20vh'}}>{t("errorMsg")}</div>
        :
          <styles.StyledTable>
            <thead>
              <styles.StyledTableRow $isHeader>
                {columns?.map((column, index) => (
                  <styles.TableColumn key={index}>
                    <div 
                      style={{display: 'flex', alignItems: 'center'}}
                      onClick={()=> handleSort(column)}
                    >
                      {t(column)}
                      {sortedBy === column && sortedOrder === 'up' && column !== 'Tags' ? 
                        <styles.ArrowUp style={{ height: '16px'}}/> 
                      : sortedBy === column && sortedOrder === 'down' && column !== 'Tags' ? 
                        <styles.ArrowDown style={{ height: '16px'}} /> 
                      : column === 'Tags' ? null : <SortArrowHollow/> }
                    </div>
                  </styles.TableColumn>
                ))}
              </styles.StyledTableRow>
            </thead>
            <tbody>
              {tableData?.slice(0, rowsPerPage)?.map((row, rowIndex) => (
                <styles.StyledTableRow key={rowIndex}>
                  {columns?.map((column, colIndex) => {              
                    let reversedDate;
                    let allTags;
                    if(column === "RelevantFromDate") {
                      reversedDate = row[column]?.split('-').reverse().join('.');
                    }
                    if(column === "Tags") {
                      allTags = row[column]?.trim().split(';');
                    }
                    
                    const isLastColumn = colIndex === columns.length - 1;
                    
                    return(
                      <styles.TableData 
                        key={colIndex} 
                        onClick={() => moveToHtml(row.ArticleID, rowIndex)}
                      >
                        {isLastColumn ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                              <span style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '5px' }}>
                                <AllTags tags={allTags}/> 
                              </span>
                            </div>
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, row)}
                              style={{ padding: '4px', marginLeft: '8px' }}
                            >
                              <MoreVertIcon style={{ fontSize: '20px' }} />
                            </IconButton>
                          </div>
                        ) : (
                          <>
                            {column === 'RelevantFromDate' && <img src={`./${row['MainClassificationName']}.png`} alt='' style={{width: '33px', borderRadius: '50%', verticalAlign: 'middle'}}/>}
                            <span style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '5px' }}>
                              {column === "RelevantFromDate" ? reversedDate : row[column]}
                            </span>
                          </>
                        )}
                      </styles.TableData>
                    );
                  })}
                </styles.StyledTableRow>
              ))}
            </tbody>
          </styles.StyledTable>
        }

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <MenuItem onClick={handleCopyLink} style={{ gap: 5 }} >
            {t('copyLink')}
            <styles.StyledShareIcon />
          </MenuItem>
        </Menu>

        {copiedMounted && copiedPosition && (
          <styles.CopiedPill 
            visible={copiedVisible}
            style={{
              position: 'fixed',
              top: `${copiedPosition.top}px`,
              left: `${copiedPosition.left}px`,
              transform: 'translateY(-50%)',
              zIndex: 9999,
            }}
          >
            {t('linkCopied')}
          </styles.CopiedPill>
        )}

        <styles.PaginationContainer>
          <Stack spacing={2}>
            <Pagination
              count={tableData?.length > rowsPerPage ? currentPage + 1 : currentPage}
              page={currentPage}
              onChange={handlePageChange}
              siblingCount={1}
              boundaryCount={1}
              variant="outlined"
              shape="rounded"
              style={{
                direction: isRTL ? 'rtl' : 'ltr',
                alignSelf: 'center',
                bottom: '12px',
              }}
              renderItem={(item) => (
                <PaginationItem
                  {...item}
                  style={{
                    maxHeight: '24px',
                    borderRadius: '4px',
                    backgroundColor: item.selected ? '#2196F3' : 'white',
                    color: item.selected ? 'white' : '#494949',
                    border: item.selected ? '1px solid #2196F3': '1px solid #EEEEEE'
                  }}
                />
              )}
              disabled={isLoading}
            />
          </Stack>
        </styles.PaginationContainer>
      </styles.TableWrapper>
    </styles.BodyWrapper>
  );
};

export default Table;
